import { Button, Icon } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import classNames from "classnames";
import { Fragment, useCallback } from "react";
import { Accept, ErrorCode, FileRejection, useDropzone } from "react-dropzone";
import { FileChip } from "./FileChip";
import css from "./FileUpload.module.scss";

type FileUploadSize = "small" | "large";

interface FileUploadBaseProps {
    accept?: Accept;
    maxSize?: number;
    onReject?: (message: string) => void;
    prompt?: string;
    size?: FileUploadSize;
}

interface SingleFileUploadProps extends FileUploadBaseProps {
    multiple?: false;
    file: File | null;
    onFileSelected: (file: File) => void;
    onClearFile: () => void;
}

/** Keeps the drop target open and hands every accepted file to the caller, who lists them. */
interface MultipleFileUploadProps extends FileUploadBaseProps {
    multiple: true;
    onFilesSelected: (files: ReadonlyArray<File>) => void;
}

type FileUploadProps = SingleFileUploadProps | MultipleFileUploadProps;

const getRejectionMessage = (
    rejection: FileRejection,
    accept: Accept | undefined,
    maxSize: number | undefined
): string => {
    const codes = new Set(rejection.errors.map((error) => error.code));
    if (codes.has(ErrorCode.FileTooLarge) && maxSize !== undefined) {
        const maxMb = maxSize / (1024 * 1024);
        return `File is too large. Max size is ${maxMb} MB.`;
    }
    if (codes.has(ErrorCode.FileInvalidType)) {
        const extensions = Object.values(accept ?? {}).flat();
        return extensions.length > 0
            ? `File must be ${extensions.join(" or ")}.`
            : "File type is not supported.";
    }
    return rejection.errors[0]?.message ?? "File could not be uploaded.";
};

export const FileUpload: React.FC<FileUploadProps> = (props) => {
    const {
        accept,
        maxSize,
        onReject,
        prompt = "Drop file or click to browse",
        size = "large"
    } = props;
    const file = props.multiple ? null : props.file;
    const isSmall = size === "small";
    const acceptedFileExtensions = Object.values(accept ?? {}).flat();
    const onDrop = useCallback(
        (
            acceptedFiles: ReadonlyArray<File>,
            fileRejections: ReadonlyArray<FileRejection>
        ) => {
            const [firstRejection] = fileRejections;
            if (firstRejection !== undefined) {
                onReject?.(
                    getRejectionMessage(firstRejection, accept, maxSize)
                );
                if (!props.multiple) {
                    return;
                }
            }

            if (props.multiple) {
                if (acceptedFiles.length > 0) {
                    props.onFilesSelected(acceptedFiles);
                }
                return;
            }

            const [first] = acceptedFiles;
            if (first !== undefined) {
                props.onFileSelected(first);
            }
        },
        [props, onReject, accept, maxSize]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept,
        multiple: props.multiple === true,
        maxSize
    });

    const removeAction = props.multiple ? null : (
        <Button
            className={css["FileUpload-remove-button"]}
            variant="minimal"
            icon={IconNames.CROSS}
            aria-label="Remove file"
            onClick={(event) => {
                event.stopPropagation();
                props.onClearFile();
            }}
        />
    );

    const fileReplacesBox = isSmall && file !== null;

    return (
        <div
            {...getRootProps({
                className: classNames(css["FileUpload-dropzone"], {
                    [css["FileUpload-dropzone-active"]]: isDragActive,
                    [css["FileUpload-dropzone-small"]]: isSmall,
                    [css["FileUpload-dropzone-filled"]]: fileReplacesBox
                })
            })}
        >
            <input {...getInputProps()} />
            {fileReplacesBox ? (
                <FileChip
                    filename={file.name}
                    actions={removeAction}
                    tone="white"
                    elevated
                    padding="very-small"
                    className={css["FileUpload-file-chip-small"]}
                />
            ) : (
                <div className={css["FileUpload-dropzone-content"]}>
                    {file !== null ? (
                        <FileChip
                            filename={file.name}
                            actions={removeAction}
                            tone="white"
                            elevated
                            className={css["FileUpload-file-chip-large"]}
                        />
                    ) : isSmall ? (
                        <strong>{prompt}</strong>
                    ) : (
                        <>
                            <Icon
                                icon={IconNames.CLOUD_UPLOAD}
                                size={28}
                                intent="primary"
                            />
                            <strong>{prompt}</strong>
                            {acceptedFileExtensions.length > 0 && (
                                <span className="bp5-text-small">
                                    {acceptedFileExtensions.map(
                                        (extension, index) => {
                                            const isLast =
                                                index ===
                                                acceptedFileExtensions.length -
                                                    1;
                                            const separator =
                                                index === 0
                                                    ? null
                                                    : isLast
                                                    ? " or "
                                                    : ", ";
                                            return (
                                                <Fragment key={extension}>
                                                    {separator}
                                                    <code className="bp5-code">
                                                        {extension}
                                                    </code>
                                                </Fragment>
                                            );
                                        }
                                    )}
                                </span>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};
